/**
 * Reel creation progress stages (MVP client machine).
 */

export type ReelStage =
  | 'idle'
  | 'prepare'
  | 'queue'
  | 'generate'
  | 'render'
  | 'encode'
  | 'mux'
  | 'ready'
  | 'failed';

export type ReelProgressMode = 'local' | 'ai' | 'video' | 'unknown';

export interface ReelProgress {
  stage: ReelStage;
  mode: ReelProgressMode;
  /** 0–100; null = indeterminate */
  percent: number | null;
  label: string;
  etaHint?: string;
  error?: string;
  startedAt: number;
}

export const IDLE_REEL_PROGRESS: ReelProgress = {
  stage: 'idle',
  mode: 'unknown',
  percent: null,
  label: '',
  startedAt: 0,
};

const STAGE_COPY: Record<
  Exclude<ReelStage, 'idle' | 'ready' | 'failed'>,
  { label: string; etaHint?: string }
> = {
  prepare: { label: 'Preparing photo…', etaHint: 'A few seconds' },
  queue: { label: 'Queued for AI motion…', etaHint: 'Usually under 3 min total' },
  generate: { label: 'Generating motion…', etaHint: 'Usually under 3 min' },
  render: { label: 'Rendering frames…', etaHint: 'Depends on video length' },
  encode: { label: 'Encoding preview…', etaHint: 'Almost done' },
  mux: { label: 'Packaging video…', etaHint: 'Almost done' },
};

export function reelProgressAt(
  stage: Exclude<ReelStage, 'idle'>,
  extras?: Partial<ReelProgress>
): ReelProgress {
  if (stage === 'ready') {
    return {
      stage: 'ready',
      mode: extras?.mode ?? 'unknown',
      percent: 100,
      label: extras?.label ?? 'Reel ready',
      startedAt: extras?.startedAt ?? Date.now(),
      ...extras,
    };
  }
  if (stage === 'failed') {
    return {
      stage: 'failed',
      mode: extras?.mode ?? 'unknown',
      percent: null,
      label: extras?.label ?? 'Reel failed',
      error: extras?.error,
      startedAt: extras?.startedAt ?? Date.now(),
      ...extras,
    };
  }
  const copy = STAGE_COPY[stage];
  return {
    stage,
    mode: extras?.mode ?? 'unknown',
    percent: extras?.percent ?? null,
    label: extras?.label ?? copy.label,
    etaHint: extras?.etaHint ?? copy.etaHint,
    startedAt: extras?.startedAt ?? Date.now(),
    ...extras,
  };
}

/** Estimated AI stages while the callable is in flight. */
export function estimatedAiStage(elapsedMs: number): Exclude<ReelStage, 'idle' | 'ready' | 'failed'> {
  if (elapsedMs < 2500) return 'prepare';
  if (elapsedMs < 12000) return 'queue';
  return 'generate';
}

export const REEL_STAGE_CHIPS: { id: ReelStage; label: string }[] = [
  { id: 'prepare', label: 'Photo' },
  { id: 'queue', label: 'Queue' },
  { id: 'generate', label: 'Generate' },
  { id: 'encode', label: 'Encode' },
];
